import { z } from "zod";
import { ApiClient, ApiError } from "../../../api/api-client";
import { addressSchema, hex32Schema, uint256Schema, rewardRateSchema, rewardDenominatorSchema } from "../../../api/schemas";
import { normalizeReward, type RewardView } from "../../../api/reward-adapter";

const uint256 = uint256Schema;
const index = z.number().int().nonnegative().safe();
export const mintHistoryItemSchema = z.object({
  id: z.string().min(1), type: z.literal("MINT"), tokenId: uint256,
  wallet: addressSchema, sourceToken: addressSchema,
  tierUsd: z.number().nonnegative().finite().nullable(),
  rewardBps: index.max(10_000).nullable(),
  rewardPolicyVersion: index.positive().nullable().optional(),
  rewardRate: rewardRateSchema.nullable().optional(),
  rewardDenominator: rewardDenominatorSchema.nullable().optional(),
  nftStatus: z.enum(["PENDING_RANDOMNESS", "RANDOMNESS_READY", "FINALIZED", "BURNED", "UNKNOWN"]).nullable(),
  mintFeeWei: uint256.nullable(), payoutWei: uint256.nullable(),
  poolBeforeWei: uint256.nullable(), poolAfterWei: uint256.nullable(),
  txHash: hex32Schema, blockNumber: index, blockHash: hex32Schema,
  transactionIndex: index, logIndex: index, timestamp: z.iso.datetime().nullable(),
  confirmationStatus: z.literal("CONFIRMED"),
});
export const mintHistorySchema = z.object({
  chainId: z.number().int().positive().safe(), collectionAddress: addressSchema,
  scope: z.literal("GLOBAL"), historyAvailable: z.boolean(),
  dataStatus: z.enum(["CURRENT", "CATCHING_UP", "STALE", "UNAVAILABLE"]),
  blockingReasons: z.array(z.string()),
  indexedThrough: z.object({ blockNumber: index, blockHash: hex32Schema,
    updatedAt: z.iso.datetime() }).nullable(),
  items: z.array(mintHistoryItemSchema).nullable(), nextCursor: z.string().min(1).nullable(),
}).superRefine((data, ctx) => {
  const unavailable = data.dataStatus === "UNAVAILABLE";
  if (data.historyAvailable === unavailable || (data.items === null) !== unavailable ||
    unavailable && data.nextCursor !== null) {
    ctx.addIssue({ code: "custom", message: "Inconsistent history availability" });
  }
});
export type MintHistoryItemDto = z.infer<typeof mintHistoryItemSchema>;
export type MintHistoryPageDto = z.infer<typeof mintHistorySchema>;
export type MintHistoryItem = Omit<MintHistoryItemDto, keyof RewardView> & RewardView;
export type MintHistoryPage = Omit<MintHistoryPageDto, "items"> & { items: MintHistoryItem[] | null };
export function mintHistoryView(page: MintHistoryPageDto): MintHistoryPage {
  return { ...page, items: page.items?.map(item => ({ ...item,
    ...normalizeReward(item, item.nftStatus !== "PENDING_RANDOMNESS" && item.nftStatus !== "RANDOMNESS_READY"),
    rewardBps: item.rewardPolicyVersion === 1 ? item.rewardBps : null,
  })) ?? null };
}
export interface MintHistoryContext {
  api: ApiClient;
  chainId: number;
  collectionAddress: string;
  environment: string;
}

export async function readMintHistory(context: MintHistoryContext, cursor: string | null, signal: AbortSignal): Promise<MintHistoryPage> {
  const query = new URLSearchParams({ type: "MINT", limit: "20" });
  if (cursor !== null) query.set("cursor", cursor);
  const page = await context.api.get(`/nfts/history?${query}`, mintHistorySchema, signal);
  if (page.chainId !== context.chainId || page.collectionAddress.toLowerCase() !== context.collectionAddress.toLowerCase()) {
    throw new ApiError(502, "NFT_HISTORY_CONTEXT_MISMATCH", "History chain or collection does not match this application.", null, null);
  }
  return mintHistoryView(page);
}
